import React, { useEffect, useRef, useState } from "react";
import { TFieldDefClass } from "../types/api/APIClassTypes";
import { TReservationSearchOptions } from "../types/components/SearchOptions";
import { ReservationSearch } from "../models/ReservationSearch";
import { Limits } from "../models/Limits";
import {
    TListCalendarInjectedProps,
    TListCalendarWrapperProps,
} from "../types/components/ListCalendarWrapper";
import { MillenniumDate, MillenniumDateTime } from "@timeedit/millennium-time";
import ReservationListSettings from "./ReservationListSettings";
import Language from "../lib/Language";
import _ from "underscore";
import TemplateKind from "../models/TemplateKind";
import { Reservation } from "../models/Reservation";
import ReservationTypeList from "./ReservationTypeList";
import RC from "../lib/ReservationConstants";
import API from "../lib/TimeEditAPI";
import { useAppContext } from "../contexts/AppContext";

export const COL_IDS = {
    ID: 0,
    DATE: 1,
    TIME: 2,
    LENGTH: 10,
    TYPES: 3,
    FIELDS: 4,
};

const getAvailableColumns = () => {
    const columns = [
        { name: Language.get("cal_reservation_list_column_id"), sortable: true, id: 0 },
        { name: Language.get("cal_reservation_list_column_date"), sortable: true, id: 1 },
        { name: Language.get("cal_reservation_list_column_time"), sortable: false, id: 2 },
        { name: Language.get("cal_reservation_list_column_length"), sortable: false, id: 10 },
        { name: Language.get("cal_reservation_list_column_type_all"), sortable: false, id: 3 },
        { name: Language.get("cal_reservation_list_column_field_all"), sortable: false, id: 4 },
        { name: Language.get("cal_reservation_list_column_created"), sortable: true, id: 5 },
        { name: Language.get("cal_reservation_list_column_modified"), sortable: true, id: 6 },
        { name: Language.get("cal_reservation_list_column_week"), sortable: false, id: 8 },
        { name: Language.get("cal_reservation_list_column_week_day"), sortable: false, id: 9 },
        { name: Language.get("cal_reservation_list_column_status"), sortable: false, id: 11 },
        { name: Language.get("nc_reservation_exception_title"), sortable: false, id: 12 },
        { name: Language.get("nc_tags_title"), sortable: true, id: 13 },
        {
            name: Language.get("nc_reservation_group_list_title"),
            sortable: false,
            id: 14,
        },
        {
            name: Language.get("cal_res_side_user_time_zone"),
            sortable: false,
            id: 15,
        },
        { name: Language.get("nc_capacity"), sortable: false, id: 16 },
        { name: Language.get("nc_remaining_capacity"), sortable: false, id: 17 },
        { name: Language.get("nc_size"), sortable: false, id: 18 },
    ];
    return columns;
};

type TReservationListProps = TListCalendarWrapperProps<ReservationSearch> &
    TListCalendarInjectedProps;

const DEFAULT_TIME_LIMITS = () => ({
    begin: MillenniumDateTime.fromDate(MillenniumDate.today()).getMts(),
    end: 0,
});

// `data` arrives via ListCalendar's cloneElement, so it is absent when the child
// is rendered directly. The class relied on the try/catch swallowing the
// resulting TypeError; the guard makes that explicit.
const getTimeLimits = (data?: { limits?: Limits }) => {
    if (!data?.limits) {
        return DEFAULT_TIME_LIMITS();
    }
    try {
        return {
            begin: MillenniumDateTime.fromDate(data.limits.getStartDate()).getMts(),
            end: MillenniumDateTime.fromDate(data.limits.getEndDate()).getMts(),
        };
    } catch (error) {
        return DEFAULT_TIME_LIMITS();
    }
};

const limitTo = (categories, allowedCategories) =>
    _.omit(
        categories,
        (value, category) =>
            !_.some(
                allowedCategories,
                (allowedCategory) =>
                    String(allowedCategory.id) === category &&
                    _.every(value, (val) => _.contains(allowedCategory.categories, val))
            )
    );

const ReservationList = (props: TReservationListProps) => {
    const appContext = useAppContext();

    // One state object rather than separate useState calls: componentDidMount
    // passed this.setState straight to API.getObjectCategories, relying on the
    // class's partial merge. useState replaces, so the merge is explicit below.
    const [state, setState] = useState(() => {
        const timeLimit = getTimeLimits(props.data);
        return {
            validStatus: [] as number[],
            allStatus: [
                RC.STATUS.CONFIRMED,
                RC.STATUS.PRELIMINARY,
                RC.STATUS.PLANNED,
                RC.STATUS.REQUESTED,
                RC.STATUS.REJECTED,
            ],
            searchOptions: {
                templateKind: TemplateKind.RESERVATION,
                searchString: "",
                beginTime: timeLimit.begin,
                endTime: timeLimit.end,
                name: "",
                allReservations: true,
                reservationStatus: [
                    RC.STATUS.CONFIRMED,
                    RC.STATUS.PRELIMINARY,
                    RC.STATUS.PLANNED,
                    RC.STATUS.REQUESTED,
                    RC.STATUS.REJECTED,
                ],
                includeMembers: RC.MEMBERS.EXCLUDE,
                useTemplateGroup: false,
                templateGroup: 0,
                reservationVariant: RC.VARIANT.RESERVATION,
                complete: true,
                incomplete: true,
                permission: RC.PERMISSION.READ,
                objectCategories: {},
                reservationCategories: {},
                ignoreExceptions: false,
                reservationGroupsOnly: false,
                placeholderType: null,
            } as TReservationSearchOptions,
            progress: null,
            availableReservationCategories: [] as TFieldDefClass[],
            availableReservationCheckboxes: [] as TFieldDefClass[],
            availableObjectCategories: [] as TFieldDefClass[],
            availableObjectCheckboxes: [] as TFieldDefClass[],
        };
    });

    useEffect(() => {
        API.getObjectCategories((result) => setState((prev) => ({ ...prev, ...result })));
    }, []);

    // componentDidUpdate re-synced the time window when data.limits changed
    // identity. The inner comparison read timeLimits.endTime, which does not
    // exist -- getTimeLimits returns { begin, end } -- so the guard was always
    // true and it re-synced unconditionally. ConflictList has the correct
    // comparison; this now matches it.
    const prevLimits = useRef(props.data?.limits);
    useEffect(() => {
        if (prevLimits.current === props.data?.limits) {
            return;
        }
        prevLimits.current = props.data?.limits;
        const timeLimits = getTimeLimits(props.data);
        setState((prev) => {
            if (
                timeLimits.begin === prev.searchOptions.beginTime &&
                timeLimits.end === prev.searchOptions.endTime
            ) {
                return prev;
            }
            return {
                ...prev,
                searchOptions: _.extend({}, prev.searchOptions, {
                    beginTime: timeLimits.begin,
                    endTime: timeLimits.end,
                }),
            };
        });
    });

    const onSelectionChanged = (newSelectedReservations) => {
        const status = _.uniq(
            _.flatten(
                newSelectedReservations.map((reservation) => {
                    if (reservation.status) {
                        return reservation.status.map((st) => st.status);
                    }
                    return [RC.STATUS.CONFIRMED];
                })
            ).filter((st) => st >= RC.STATUS.CONFIRMED && st < RC.STATUS.TIME)
        );
        if (status.length === 0) {
            status.push(RC.STATUS.CONFIRMED);
        }
        Reservation.getValidStatus(status, (result) => {
            setState((prev) => ({ ...prev, validStatus: result.map((st) => st.status) }));
        });
    };

    // No current caller supplies `cb` -- GenericList always calls this with a
    // single argument -- so running it immediately rather than after commit is
    // unobservable today. Kept for signature compatibility.
    const onSearchSettingsChange = (settings, cb?: () => void) => {
        let newSettings = _.extend(
            {},
            _.omit(settings, (value) => value === undefined)
        );
        // Reservation list should always search for reservations, not cancellations
        newSettings = _.extend(newSettings, { reservationVariant: RC.VARIANT.RESERVATION });

        setState((prev) => ({
            ...prev,
            searchOptions: _.extend({}, prev.searchOptions, newSettings),
        }));
        if (cb) {
            cb();
        }
    };

    const getSettingsComponent = (
        saveDefaultSettings,
        saveSettings,
        getSavedSettings,
        removeSettings
    ) => {
        const opts = state.searchOptions;

        return (
            <ReservationListSettings
                onChange={onSearchSettingsChange}
                allReservations={opts.allReservations}
                reservationStatus={opts.reservationStatus}
                includeMembers={opts.includeMembers}
                saveDefaultSettings={saveDefaultSettings}
                saveSettings={saveSettings}
                removeSettings={removeSettings}
                getSavedSettings={getSavedSettings}
                templateKind={opts.templateKind}
                complete={opts.complete}
                incomplete={opts.incomplete}
                permission={opts.permission}
                useTemplateGroup={opts.useTemplateGroup}
                objectCategories={limitTo(
                    opts.objectCategories,
                    state.availableObjectCategories.concat(state.availableObjectCheckboxes)
                )}
                reservationCategories={limitTo(
                    opts.reservationCategories,
                    state.availableReservationCategories.concat(
                        state.availableReservationCheckboxes
                    )
                )}
                availableReservationCategories={state.availableReservationCategories}
                availableReservationCheckboxes={state.availableReservationCheckboxes}
                availableObjectCategories={state.availableObjectCategories}
                availableObjectCheckboxes={state.availableObjectCheckboxes}
                ignoreExceptions={opts.ignoreExceptions}
                reservationGroupsOnly={opts.reservationGroupsOnly}
                hasReservationExceptions={appContext.hasReservationExceptions}
                placeholderType={opts.placeholderType}
            />
        );
    };

    return (
        <ReservationTypeList
            {...props}
            onSearchSettingsChange={onSearchSettingsChange}
            onSelectionChanged={onSelectionChanged}
            searchOptions={state.searchOptions}
            defaultStatus={RC.STATUS.TIME}
            progress={state.progress}
            columns={getAvailableColumns()}
            defaultSelectedColumns={[COL_IDS.DATE, COL_IDS.TIME, COL_IDS.TYPES]}
            getSettingsComponent={getSettingsComponent}
            title={Language.get("nc_reserv_list_title")}
            allowMultiSelection
            supportsEmail
            supportsMassChange
            allStatus={state.allStatus}
            validStatus={state.validStatus}
            addTypeColumns={TemplateKind.equals(
                TemplateKind.RESERVATION,
                state.searchOptions.templateKind
            )}
            supportsBatchOperations={true}
        />
    );
};

export default ReservationList;
